import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { CheckIcon, ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { convertToCurrencyFormat, convertToTwoDecimals } from "../../../shared/utility";
import type { ReactNode } from "react";
import type { OrderItemRecord } from "../../../types/orders";

type EditableField = "quantity" | "shipped_amount" | "tax_rate" | "price" | "discount";
type SaveState = "idle" | "saving" | "saved" | "error";
const minimumSavingIndicatorMs = 700;
interface NumericConfig {
  min: number; max?: number; minFractionDigits?: number; maxFractionDigits: number; prefix?: string; suffix?: string;
}
const editableFields: Record<EditableField, NumericConfig> = {
  quantity: { min: 0.0001, maxFractionDigits: 4 },
  shipped_amount: { min: 0, maxFractionDigits: 4 },
  tax_rate: { min: 0, max: 100, maxFractionDigits: 4, suffix: "%" },
  price: { min: 0, minFractionDigits: 2, maxFractionDigits: 2, prefix: "$" },
  discount: { min: 0, minFractionDigits: 2, maxFractionDigits: 2, prefix: "$" },
};

const rightAlignedHeader = (label: ReactNode) => <span className="block w-full text-right">{label}</span>;

function SaveStatus({ status }: { status: SaveState }) {
  const labels: Record<SaveState, string> = { idle: "", saving: "Saving", saved: "Saved", error: "Save failed" };
  const label = labels[status];
  return (
    <span
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
      role={label ? "status" : undefined}
      aria-label={label}
      title={label}
    >
      {status === "saving" && (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" />
      )}
      {status === "saved" && <CheckIcon className="h-4 w-4 text-green-600" />}
      {status === "error" && <ExclamationCircleIcon className="h-4 w-4 text-red-600" />}
    </span>
  );
}

export default function OrderProductsTable({
  loading,
  values,
  onItemEditComplete,
  askDeleteOrderItem,
}: {
  loading: boolean;
  values: { order_items: OrderItemRecord[]; subtotal_dollar_amount: number; dollar_tax_amount: number; total_dollar_amount: number };
  onItemEditComplete: (data: OrderItemRecord, index: number) => Promise<{ success: boolean }>;
  askDeleteOrderItem: (row: OrderItemRecord) => void;
}) {
  const [drafts, setDrafts] = useState<Record<number, Partial<Record<EditableField, number>>>>({});
  const draftsRef = useRef<Record<number, Partial<Record<EditableField, number>>>>({});
  const [statuses, setStatuses] = useState<Record<string, SaveState>>({});
  const statusTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(
    () => () => {
      Object.values(statusTimers.current).forEach(clearTimeout);
      Object.values(saveTimers.current).forEach(clearTimeout);
    },
    [],
  );

  const cellKey = (row: OrderItemRecord, field: EditableField) => `${row.id}:${field}`;
  const draftValue = (row: OrderItemRecord, field: EditableField) => drafts[row.id]?.[field] ?? Number(row[field] ?? 0);

  const setDraftValue = (row: OrderItemRecord, field: EditableField, value: number | null) => {
    if (value === null) return;
    const nextDrafts = {
      ...draftsRef.current,
      [row.id]: { ...draftsRef.current[row.id], [field]: value },
    };
    draftsRef.current = nextDrafts;
    setDrafts(nextDrafts);
    setStatuses((current) => ({ ...current, [cellKey(row, field)]: "idle" }));
  };

  const clearDraft = (row: OrderItemRecord, field: EditableField) => {
    const rowDraft = { ...(draftsRef.current[row.id] || {}) };
    delete rowDraft[field];
    const nextDrafts = { ...draftsRef.current };
    if (Object.keys(rowDraft).length) nextDrafts[row.id] = rowDraft;
    else delete nextDrafts[row.id];
    draftsRef.current = nextDrafts;
    setDrafts(nextDrafts);
  };

  const setTemporaryStatus = (key: string, status: SaveState) => {
    if (statusTimers.current[key]) clearTimeout(statusTimers.current[key]);
    setStatuses((current) => ({ ...current, [key]: status }));
    if (status === "saved") {
      statusTimers.current[key] = setTimeout(() => {
        setStatuses((current) => ({ ...current, [key]: "idle" }));
        delete statusTimers.current[key];
      }, 1200);
    }
  };

  const commitCell = async (row: OrderItemRecord, rowIndex: number, field: EditableField) => {
    const key = cellKey(row, field);
    if (saveTimers.current[key]) {
      clearTimeout(saveTimers.current[key]);
      delete saveTimers.current[key];
    }
    const value = draftsRef.current[row.id]?.[field];
    if (value == null || Number(value) === Number(row[field] ?? 0) || statuses[key] === "saving")
      return;

    const config = editableFields[field];
    if (
      !Number.isFinite(Number(value)) ||
      Number(value) < config.min ||
      (config.max != null && Number(value) > config.max)
    ) {
      setTemporaryStatus(key, "error");
      return;
    }

    const newData = { ...row, ...(draftsRef.current[row.id] || {}), [field]: Number(value) };
    const subtotal = Math.max(
      0,
      (Number(newData.price) - Number(newData.discount)) * Number(newData.quantity)
    );
    newData.sub_dollar_amount = Number(convertToTwoDecimals(subtotal));
    newData.total_dollar_amount = Number(convertToTwoDecimals(
      subtotal * (1 + Number(newData.tax_rate || 0) / 100)
    ));

    setTemporaryStatus(key, "saving");
    const saveStartedAt = Date.now();
    const result = await onItemEditComplete(newData, rowIndex);
    const remainingIndicatorTime = minimumSavingIndicatorMs - (Date.now() - saveStartedAt);
    if (remainingIndicatorTime > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingIndicatorTime));
    }
    clearDraft(row, field);
    setTemporaryStatus(key, result?.success ? "saved" : "error");
  };

  const scheduleCommit = (row: OrderItemRecord, rowIndex: number, field: EditableField) => {
    const key = cellKey(row, field);
    if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(() => {
      delete saveTimers.current[key];
      void commitCell(row, rowIndex, field);
    }, 500);
  };

  const editableBody =
    (field: EditableField, readOnly = false) =>
    (row: OrderItemRecord, options: { rowIndex: number }) => {
      const config = editableFields[field];
      const key = cellKey(row, field);
      const handleValueChange = (value: number | null) => {
        setDraftValue(row, field, value);
        scheduleCommit(row, options.rowIndex, field);
      };
      return (
        <div className="flex w-full min-w-[7.5rem] items-center justify-end gap-1">
          <InputNumber
            value={draftValue(row, field)}
            onValueChange={
              readOnly
                ? undefined
                : (event) => handleValueChange(event.value ?? null)
            }
            onChange={readOnly ? undefined : (event) => handleValueChange(event.value ?? null)}
            onBlur={readOnly ? undefined : () => commitCell(row, options.rowIndex, field)}
            onKeyDown={
              readOnly
                ? undefined
                : (event) => {
                    event.stopPropagation();
                    if (event.key === "Enter") event.currentTarget.blur();
                    if (event.key === "Escape") {
                      clearDraft(row, field);
                      event.currentTarget.blur();
                    }
                  }
            }
            disabled={readOnly || statuses[key] === "saving"}
            className="ml-auto w-24"
            inputClassName={`w-full !text-right text-sm ${statuses[key] === "error" ? "border-red-500" : ""}`}
            inputStyle={{ textAlign: "right" }}
            useGrouping={false}
            {...config}
          />
          <SaveStatus status={readOnly ? "idle" : statuses[key]} />
        </div>
      );
    };

  const productBody = (row: OrderItemRecord) => (
    <p>
      <span className="font-semibold text-gray-900">{row.product_name}</span>
      <br />
      <span className="text-gray-700">SKU: {row.sku}</span>
    </p>
  );

  return (
    <div className="mt-5">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          {loading ? (
            <div className="h-10 w-full rounded-md border p-4 shadow">
              <div className="flex animate-pulse">Loading</div>
            </div>
          ) : (
            <div className="overflow-hidden">
              <DataTable
                value={values.order_items}
                dataKey="id"
                tableClassName="min-w-full bg-white divide-y divide-gray-300 order-items-table-parent"
                cellClassName={() => "p-2 bg-white border-1-bottom-gray text-sm"}
              >
                <Column header="Product" body={productBody} />
                <Column
                  header={rightAlignedHeader("Order quantity")}
                  field="quantity"
                  body={editableBody("quantity")}
                  headerStyle={{ textAlign: "right" }}
                  bodyStyle={{ textAlign: "right" }}
                  bodyClassName="text-right"
                />
                <Column
                  header={rightAlignedHeader("Shipped")}
                  field="shipped_amount"
                  body={editableBody("shipped_amount", true)}
                  headerStyle={{ textAlign: "right" }}
                  bodyStyle={{ textAlign: "right" }}
                  bodyClassName="text-right"
                />
                <Column
                  header={rightAlignedHeader("Unit Price")}
                  field="price"
                  body={editableBody("price")}
                  headerStyle={{ textAlign: "right" }}
                  bodyStyle={{ textAlign: "right" }}
                  bodyClassName="text-right"
                />
                <Column
                  header={rightAlignedHeader("Discount")}
                  field="discount"
                  body={editableBody("discount")}
                  headerStyle={{ textAlign: "right" }}
                  bodyStyle={{ textAlign: "right" }}
                  bodyClassName="text-right"
                />
                <Column
                  header={rightAlignedHeader("Tax %")}
                  field="tax_rate"
                  body={editableBody("tax_rate")}
                  headerStyle={{ textAlign: "right" }}
                  bodyStyle={{ textAlign: "right" }}
                  bodyClassName="text-right"
                />
                <Column
                  header={rightAlignedHeader("Total Price")}
                  body={(row: OrderItemRecord) => convertToCurrencyFormat(row.total_dollar_amount)}
                  headerStyle={{ textAlign: "right" }}
                  bodyStyle={{ textAlign: "right" }}
                  bodyClassName="text-right"
                />
                <Column
                  header="Action"
                  body={(row: OrderItemRecord) => (
                    <button
                      type="button"
                      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:border-red-300 hover:bg-red-100"
                      onClick={() => askDeleteOrderItem(row)}
                    >
                      Delete
                    </button>
                  )}
                />
              </DataTable>
            </div>
          )}
          <div className="mt-4 w-full text-right">
            <p className="font-bold">
              Subtotal: {convertToCurrencyFormat(values.subtotal_dollar_amount)}
            </p>
            <p className="font-bold">Taxes: {convertToCurrencyFormat(values.dollar_tax_amount)}</p>
            <p className="font-bold">
              Total: {convertToCurrencyFormat(values.total_dollar_amount)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
